import React from "react";

// Card media. One box on every work type, carrying the container-medium radius
// so the grid keeps a single rhythm and cards line up row by row. No inner
// stroke: the card already paints the clickable-container outline, and a second
// 1px well (especially inside Trace's 8px padding) read as a thicker rim than
// the article deck card. The radius plus the card padding is what holds a pale
// screenshot off the edge.
//
// The box takes its height from its width at a fixed ratio rather than the flat
// 320px the Figma card was drawn with. Every piece of card artwork is exported
// at 19:14, and 320px only matched that at the desktop column width — narrower
// columns made the box progressively squarer than the artwork, which any fit
// rule then has to pay for in either cropped artwork or bare plate. Deriving the
// height instead keeps the frame the artwork's own shape at every width, so the
// artwork lands in it exactly. Cards in a row share a width, so they still share
// a height.
//
// The box uses the DS `container-featured-gradient` as its backdrop (a
// top-to-bottom gradient from container-background-featured start to end), so
// transparent artwork sits on a themed surface rather than a hard neutral.
// An item with no media renders that gradient on its own as the placeholder.
//
// Nothing is overlaid on the artwork: the thumbnail is the whole of what the
// card says about the media.

// Videos are stored in /public/videos as matching .webm + .mp4 pairs and are
// referenced without an extension, so both codecs can be offered to the browser.
// The webm comes first on purpose: it is the one that keeps its alpha channel,
// and every browser that understands it will take it. The mp4 is the fallback
// for the ones that do not.
//
// Each clip also has a same-name `.webp` first frame. Browsers that skip
// autoplay or never decode a video frame (Safari Low Power Mode, data-saver,
// webm-only clips on Safari) would otherwise paint an empty box; the poster
// is that frame until the video can play.
export const VIDEO_SOURCES = [
  { ext: "webm", type: "video/webm" },
  { ext: "mp4", type: "video/mp4" },
];

// These clips stand in for GIFs: muted, looping, no controls anywhere, so a
// video card and an animated-GIF card behave the same.
export const MediaVideo = React.forwardRef(function MediaVideo(
  { src, className = "", style, autoPlay = true, onClick, poster, ...rest },
  ref
) {
  return (
    <video
      ref={ref}
      className={className}
      style={style}
      onClick={onClick}
      autoPlay={autoPlay}
      muted
      loop
      playsInline
      preload="metadata"
      poster={poster ?? `${src}.webp`}
      {...rest}
    >
      {VIDEO_SOURCES.map((source) => (
        <source key={source.ext} src={`${src}.${source.ext}`} type={source.type} />
      ))}
    </video>
  );
});

// The target that opens an image in the dialog: a clear button over the whole
// image, so the image is one target the pointer cannot fall through. Nothing
// is drawn over the artwork; the magnifier cursor and the image's slight lift
// (.pf-expandable in research-article.css, the homepage stage's hover) are
// the whole affordance. It sits at z-20, above a card's own full-bleed
// target, so it wins the click.
export function ExpandOverlay({
  onExpand,
  label = "Open preview",
  radius = "var(--container-medium-radius)",
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onExpand();
      }}
      className="absolute inset-0 z-20 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-stroke-focus)]"
      style={{ borderRadius: radius }}
    />
  );
}

// The ratio every piece of card artwork is exported at: 2432x1792, 1216x896 and
// 3344x2464 all reduce to it.
const MEDIA_RATIO = "19 / 14";

// One fit rule for every card, so a row reads as one row: the artwork fills the
// box, keeps its ratio, and the box crops anything over. On artwork that is
// already 19:14 there is nothing to crop; the two Rive clips are 4:3 and lose
// about six pixels off the top and bottom edge of the frame they are drawn in.
const FIT_CLASS = "h-full w-full object-cover";

function WorkMedia({
  media,
  alt = "",
  aspectRatio = MEDIA_RATIO,
  className = "",
}) {
  // Artwork with transparency (the alpha webm clips) brings its own opaque
  // surface, otherwise the themed gradient would show through the video.
  const surface =
    (media && media.background) ||
    "linear-gradient(180deg, var(--container-featured-gradient))";

  return (
    <div
      className={`group/media relative w-full overflow-hidden ${className}`}
      style={{
        aspectRatio,
        borderRadius: "var(--container-medium-radius)",
        background: surface,
      }}
    >
      {media && (
        <div className="absolute inset-0">
          {media.type === "video" ? (
            <MediaVideo src={media.src} className={FIT_CLASS} />
          ) : (
            <img src={media.src} alt={alt} loading="lazy" className={FIT_CLASS} />
          )}
        </div>
      )}
    </div>
  );
}

export default WorkMedia;
